import React, { useState, useEffect, useCallback } from 'react';
import { IEvaluation, IAnswer, IUnansweredQuestion } from 'models/EmployeeWorkshopEvaluation';
import { Feature } from "models/api";
import { QueryClient, useMutation } from 'react-query';
import { getEvaluationQuestions } from 'framework/workshopEvaluation';
import { useParams } from 'react-router-dom';
import { useAlert } from 'contexts/alertContext';
import { handleError } from 'helpers/utils';
import { addEmployeeAnswer, updateElapsedTime, updateEvaluationStatus } from 'framework/employeeWorkshopEvaluation';
import { IEmployeeWorkshopEvaluation } from 'models/employeeWorkshopEvaluationDetails';
import { Box, Button, Typography, RadioGroup, FormControlLabel, Radio, List, ListItem, Stack, Skeleton, TextField } from '@mui/material';
import ProgressBar from 'components/UI/ProgressBar';

interface Props {
  feature: Feature;
}

const formatTime = (seconds: number) => {
  const clampedSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(clampedSeconds / 60);
  const remainingSeconds = clampedSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const EmployeeWorkshopEvaluation: React.FC<Props> = ({ feature }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<IAnswer[]>([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState<IUnansweredQuestion[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [errorOccured, setErrorOccured] = useState(false);
  const [employeeWorkshopEvaluation, setEmployeeWorkshopEvaluation] = useState<IEvaluation>({} as IEvaluation);
  const [assessmentCompleted, setEvaluationCompleted] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const queryClient = new QueryClient();
  const urlParams = useParams();
  const { setAlert } = useAlert();
  let id: string = urlParams[`${feature.singleName}Id`] as string;

  const addAnswer = useMutation({
    mutationFn: (data: { answer: IAnswer }) => addEmployeeAnswer(data.answer, id),
  });

  const updatedTime = useMutation({
    mutationFn: () => updateElapsedTime(id),
  });

  const setEvaluationAsCompleted = useMutation({
    mutationFn: () => updateEvaluationStatus(id),
  });

  const finishedEvaluation = useMutation({
    mutationFn: () => updateEvaluationStatus(id),
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await queryClient.fetchQuery(
        "get Workshop Evaluation Details",
        () => getEvaluationQuestions('/getquestions?workshopId=' + id)
      );
      setEmployeeWorkshopEvaluation(response);

      if (response) {
        setEvaluationCompleted(response.isCompleted ?? false);

        const processedAnswers: IAnswer[] = response.questions.map((question: any) => {
          if (question.questionType === 2) {
            return {
              questionId: question.questionId,
              answerText: question.answerText || "",
              answered: !!question.answerText,
            };
          } else {
            const selectedOption = question.options.find((option: any) => option.isSelected);
            return {
              questionId: question.questionId,
              selectedOptionId: selectedOption ? selectedOption.optionId : undefined,
              answered: !!selectedOption,
            };
          }
        });

        setAnswers(processedAnswers);
        setElapsedTime(response.elapsedTime || 0);
      }

      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setErrorOccured(true);
      setLoading(false);
    }
  }, [queryClient, id, setAlert]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (assessmentCompleted || employeeWorkshopEvaluation?.isCompleted) return;

    const incrementTimer = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(incrementTimer);
  }, [assessmentCompleted, employeeWorkshopEvaluation?.isCompleted]);

  const totalTime = employeeWorkshopEvaluation?.timeToComplete ?? 0;

  useEffect(() => {
    if (assessmentCompleted || employeeWorkshopEvaluation?.isCompleted) return;

    if (totalTime > 0 && elapsedTime >= totalTime) {
      finishedEvaluation.mutate();
      setEvaluationAsCompleted.mutate();
      setEvaluationCompleted(true);
      setEmployeeWorkshopEvaluation((prev) => ({
        ...prev,
        isCompleted: true,
      }));
    }
  }, [elapsedTime, assessmentCompleted, totalTime, finishedEvaluation, setEvaluationAsCompleted, employeeWorkshopEvaluation?.isCompleted]);

  useEffect(() => {
    if (assessmentCompleted || employeeWorkshopEvaluation?.isCompleted) return;

    const apiTimer = setInterval(async () => {
      await updatedTime.mutateAsync();
    }, 5000);

    return () => clearInterval(apiTimer);
  }, [assessmentCompleted, employeeWorkshopEvaluation?.isCompleted]);

  if (loading) return <Stack spacing={2} p={2}>
    {Array.from({ length: 10 }).map((_, i) => (
      <Skeleton variant="rectangular" key={i} width="100%" height={30} />
    ))}
  </Stack>;

  if (!employeeWorkshopEvaluation) return null;

  const goToQuestion = (index: number) => {
    if (index < 0 || index >= employeeWorkshopEvaluation.questions.length) return;

    const currentQuestionId = employeeWorkshopEvaluation.questions[currentQuestionIndex].questionId;
    const answered = answers.some(answer => answer.questionId === currentQuestionId && answer.answered);

    setUnansweredQuestions((prevUnanswered) => {
      if (!answered) {
        return prevUnanswered.some(q => q.questionId === currentQuestionId)
          ? prevUnanswered
          : [...prevUnanswered, { questionId: currentQuestionId, questionOrder: currentQuestionIndex + 1 }];
      } else {
        return prevUnanswered.filter(q => q.questionId !== currentQuestionId);
      }
    });

    setCurrentQuestionIndex(index);

    fetchData()
  };

  const saveAnswer = async (questionId: string, value: string, type: 1 | 2) => {
    setAnswers((prevAnswers): IAnswer[] => {
      const updatedAnswers = prevAnswers.filter(answer => answer.questionId !== questionId);

      const newAnswer: IAnswer = {
        questionId,
        ...(type === 1
          ? { selectedOptionId: value }
          : { answerText: value }),
        answered: true,
      };

      return [...updatedAnswers, newAnswer];
    });

    const answer: IAnswer = {
      questionId,
      ...(type === 1
        ? { selectedOptionId: value }
        : { answerText: value }),
      answered: true,
    };

    await addAnswer.mutateAsync({ answer: answer });
    setUnansweredQuestions((prevUnanswered) =>
      prevUnanswered.filter(q => q.questionId !== questionId)
    );
  };

  const finishEvaluation = async () => {
    if (unansweredQuestions.length > 0) {
      const confirmFinish = window.confirm("There are unanswered questions. Are you sure you want to finish?");
      if (!confirmFinish) return;
    }
    finishedEvaluation.mutate();
    setEmployeeWorkshopEvaluation((prev) => ({
      ...prev,
      isCompleted: true,
    }));
  };

  const currentQuestion =
    employeeWorkshopEvaluation?.questions?.[currentQuestionIndex] || null;

  const selectedAnswer = currentQuestion
    ? answers.find(answer => answer.questionId === currentQuestion.questionId && answer.answered)?.selectedOptionId
    : null;

  const selectedAnswerText = currentQuestion
    ? answers.find(answer => answer.questionId === currentQuestion.questionId)?.answerText
    : null;

  const totalQuestions = employeeWorkshopEvaluation?.questions?.length || 0;
  const answeredQuestions = answers.filter(answer => answer.answered).length;
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const remainingSeconds = Math.max(totalTime - elapsedTime, 0);

  return (
    <>

      {
        errorOccured ? (
          <Typography sx={{ padding: 3 }} variant="h2" gutterBottom>
            An error has occured!
          </Typography>
        ) :
          employeeWorkshopEvaluation.isCompleted ? (
            <Typography sx={{ padding: 3 }} variant="h2" gutterBottom>
              You have finished your assessment.
            </Typography>
          ) : (
            <Box sx={{ padding: 3, border: '1px solid #ddd', borderRadius: 2 }}>
              <Typography variant="h4" gutterBottom>
                {employeeWorkshopEvaluation.title}
              </Typography>

              <Typography variant="body1" gutterBottom>
                <strong>Remaining Time:</strong> {formatTime(remainingSeconds)}
              </Typography>

              <ProgressBar progress={progress} />

              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {currentQuestion!.questionOrder}. {currentQuestion!.questionTitle}
                </Typography>

                {currentQuestion.questionType === 1 ? <RadioGroup
                  value={selectedAnswer}
                  onChange={(e) => saveAnswer(currentQuestion.questionId, e.target.value, 1)}
                >
                  {currentQuestion.options.map((option) => (
                    <FormControlLabel
                      key={option.optionId}
                      value={option.optionId}
                      control={<Radio />}
                      label={option.optionDescription}
                    />
                  ))}
                </RadioGroup> : <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Type your answer here..."
                  value={selectedAnswerText || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setAnswers((prevAnswers) => {
                      const updatedAnswers = prevAnswers.map((answer) =>
                        answer.questionId === currentQuestion.questionId
                          ? { ...answer, answerText: newValue }
                          : answer
                      );
                      return updatedAnswers;
                    });
                  }}
                  onBlur={(e) => {
                    const newValue = e.target.value;
                    saveAnswer(currentQuestion.questionId, newValue, 2);
                  }}
                />
                }

              </Box>

              <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => goToQuestion(currentQuestionIndex - 1)}
                  disabled={currentQuestionIndex === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ borderRadius: 0 }}
                  onClick={() => goToQuestion(currentQuestionIndex + 1)}
                  disabled={currentQuestionIndex === employeeWorkshopEvaluation.questions.length - 1}
                >
                  Next
                </Button>
                <Button variant="contained" color="success" onClick={finishEvaluation}>
                  Finish
                </Button>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>
                  Unanswered Questions:
                </Typography>
                <List>
                  {unansweredQuestions.map((q) => (
                    <ListItem
                      key={q.questionId}
                      button
                      onClick={() => goToQuestion(q.questionOrder - 1)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      Question {q.questionOrder}
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
    </>
  );
};

export default EmployeeWorkshopEvaluation;
